import {
  matchMutation,
  useMutation,
  useQueryClient,
  type DefaultError,
  type UseMutationResult,
} from "@tanstack/react-query";
import {
  compsTableGetCompsTableQueryKey,
  proposalsDeleteProposalCommissionMutation,
  useApi,
  useHeyApi,
  type BtxRequestOptions,
  type ProposalsDeleteProposalCommissionData,
  type ProposalsDeleteProposalCommissionResponse,
} from "@platform/api";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  materializeProposalCommission,
  updateCompsColumnFreeText,
  updateProposalCommission,
  updateProposalFreeText,
  type CompsColumnKind,
  type MaterializeCommissionPayload,
  type UpdateProposalCommissionPayload,
  type UpdateProposalFreeTextPayload,
} from "./client";

import type { AxiosError } from "axios";

/** Options shared by every Comps write hook. */
export interface UseCompsWriteOptions {
  rfpId: string;
  /** Runs once for every write that fails, even when a later write is in flight. */
  onError: (error: Error) => void;
}

/**
 * Every Comps write shares this mutation key, so the Comps table reload can
 * tell whether any write is still in flight.
 */
const compsWriteMutationKey = (rfpId: string): readonly string[] => [
  "comps-write",
  rfpId,
];

const useCompsTableQueryKey = (
  rfpId: string,
): ReturnType<typeof compsTableGetCompsTableQueryKey> => {
  const { centroPortalClient } = useHeyApi();

  return useMemo(
    () =>
      compsTableGetCompsTableQueryKey({
        path: { id: rfpId },
        client: centroPortalClient,
      }),
    [rfpId, centroPortalClient],
  );
};

/**
 * Options every Comps write hook passes to useMutation. Starting a write
 * cancels any Comps table reload in flight: that reload may have read the
 * table before this write commits, and would land the old value over the
 * analyst's edit.
 */
const useCompsWriteLifecycle = (
  rfpId: string,
): { mutationKey: readonly string[]; onMutate: () => Promise<void> } => {
  const queryClient = useQueryClient();
  const queryKey = useCompsTableQueryKey(rfpId);

  return {
    mutationKey: compsWriteMutationKey(rfpId),
    onMutate: () => queryClient.cancelQueries({ queryKey }),
  };
};

/**
 * Reloads the Comps table once no Comps write is in flight, so the grid shows
 * what every write committed, including the plan a materialize created. Mount
 * it once, beside the Comps table query.
 *
 * The check runs from the mutation cache, after React Query marks a write
 * finished. A write's own onSettled runs before that, so two writes finishing
 * together would each still count the other, and neither would reload.
 */
export const useReloadCompsTableAfterWrites = (rfpId: string): void => {
  const queryClient = useQueryClient();
  const queryKey = useCompsTableQueryKey(rfpId);

  useEffect(() => {
    const mutationKey = compsWriteMutationKey(rfpId);
    const isWriteInFlight = (): boolean =>
      queryClient.isMutating({ mutationKey }) > 0;

    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      const isFinishedWrite =
        event.type === "updated" &&
        (event.action.type === "success" || event.action.type === "error") &&
        matchMutation({ mutationKey }, event.mutation);
      if (isFinishedWrite && !isWriteInFlight()) {
        void queryClient.invalidateQueries({ queryKey });
      }
    });

    return () => {
      unsubscribe();
      // Nothing hears a write that finishes after the Comps tab unmounts, so
      // mark the table stale for the next visit to reload it.
      if (isWriteInFlight()) {
        void queryClient.invalidateQueries({ queryKey, refetchType: "none" });
      }
    };
  }, [queryClient, queryKey, rfpId]);
};

export interface UpdateProposalCommissionVariables {
  proposalId: string;
  proposalPlanId: string;
  payload: UpdateProposalCommissionPayload;
}

export const useUpdateProposalCommission = ({
  rfpId,
  onError,
}: UseCompsWriteOptions): UseMutationResult<
  void,
  Error,
  UpdateProposalCommissionVariables,
  void
> => {
  const axios = useApi();
  const lifecycle = useCompsWriteLifecycle(rfpId);

  return useMutation<void, Error, UpdateProposalCommissionVariables, void>({
    ...lifecycle,
    mutationFn: ({ proposalId, proposalPlanId, payload }) =>
      updateProposalCommission(
        rfpId,
        proposalId,
        proposalPlanId,
        payload,
        axios,
      ),
    onError,
  });
};

export const useDeleteProposalCommission = ({
  rfpId,
  onError,
}: UseCompsWriteOptions): UseMutationResult<
  ProposalsDeleteProposalCommissionResponse,
  AxiosError<DefaultError>,
  BtxRequestOptions<ProposalsDeleteProposalCommissionData>,
  void
> => {
  const { btxClient } = useHeyApi();
  const lifecycle = useCompsWriteLifecycle(rfpId);

  return useMutation<
    ProposalsDeleteProposalCommissionResponse,
    AxiosError<DefaultError>,
    BtxRequestOptions<ProposalsDeleteProposalCommissionData>,
    void
  >({
    ...proposalsDeleteProposalCommissionMutation({ client: btxClient }),
    ...lifecycle,
    onError,
  });
};

export interface UpdateProposalFreeTextVariables {
  proposalId: string;
  payload: UpdateProposalFreeTextPayload;
}

export const useUpdateProposalFreeText = ({
  rfpId,
  onError,
}: UseCompsWriteOptions): UseMutationResult<
  void,
  Error,
  UpdateProposalFreeTextVariables,
  void
> => {
  const axios = useApi();
  const lifecycle = useCompsWriteLifecycle(rfpId);

  return useMutation<void, Error, UpdateProposalFreeTextVariables, void>({
    ...lifecycle,
    mutationFn: ({ proposalId, payload }) =>
      updateProposalFreeText(rfpId, proposalId, payload, axios),
    onError,
  });
};

export interface UpdateCompsColumnFreeTextVariables {
  columnKind: CompsColumnKind;
  payload: UpdateProposalFreeTextPayload;
}

export const useUpdateCompsColumnFreeText = ({
  rfpId,
  onError,
}: UseCompsWriteOptions): UseMutationResult<
  void,
  Error,
  UpdateCompsColumnFreeTextVariables,
  void
> => {
  const axios = useApi();
  const lifecycle = useCompsWriteLifecycle(rfpId);

  return useMutation<void, Error, UpdateCompsColumnFreeTextVariables, void>({
    ...lifecycle,
    mutationFn: ({ columnKind, payload }) =>
      updateCompsColumnFreeText(rfpId, columnKind, payload, axios),
    onError,
  });
};

/** Guard key for a materialize: one analyst plan per (carrier, plan group). */
const materializeKey = (payload: MaterializeCommissionPayload): string =>
  `${payload.carrierId}:${payload.rfpBenefitPlanGroupId}`;

/**
 * Materializes an analyst proposal plan + commission for an unquoted Comps cell
 * (ADR-024), guarding against duplicate submissions.
 *
 * The materialize endpoint always appends a new plan group (it is not idempotent
 * per plan group). Until a Comps table reload that includes the new plan reaches
 * the grid, the edited cell still renders as unquoted, with no proposalPlanId to
 * PATCH, and another edit would materialize a duplicate plan. So a
 * `(carrier, group)` key stays guarded from submit on, and is released only when
 * the materialize fails. Once the grid shows the plan, Comps PATCHes that cell
 * and no longer calls materialize for it.
 */
export const useMaterializeCompsCommission = ({
  rfpId,
  onError,
}: UseCompsWriteOptions): {
  materialize: (payload: MaterializeCommissionPayload) => void;
} => {
  const axios = useApi();
  const lifecycle = useCompsWriteLifecycle(rfpId);
  const guardedKeys = useRef(new Set<string>());

  const { mutate } = useMutation<
    void,
    Error,
    MaterializeCommissionPayload,
    void
  >({
    ...lifecycle,
    mutationFn: (payload) =>
      materializeProposalCommission(rfpId, payload, axios),
    onError: (error, payload) => {
      guardedKeys.current.delete(materializeKey(payload));
      onError(error);
    },
  });

  const materialize = useCallback(
    (payload: MaterializeCommissionPayload) => {
      const key = materializeKey(payload);
      if (guardedKeys.current.has(key)) return;
      guardedKeys.current.add(key);
      mutate(payload);
    },
    [mutate],
  );

  return { materialize };
};
